import { EditorContent } from '@tiptap/react';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
	BoldIcon,
	BracesIcon,
	ChevronDownIcon,
	ItalicIcon,
	LinkIcon,
	ListIcon,
	ListOrderedIcon,
	UnderlineIcon,
} from './icons';
import { applyRichTextLink, type IRichTextSelection } from './link';
import type { IMergeFieldToken } from './mergeFieldSerialization';
import type { IMergeFieldAttrs } from './mergeFieldToken';
import { CONTACT_MERGE_FIELDS } from './placeholders';
import { styles } from './styles';
import { type IRichTextChangeMeta, useRichTextEditor } from './useRichTextEditor';

/**
 * Stamped on every paragraph, mirroring legacy TinyMCE's forced_root_block_attrs
 * (DefaultRawRichTextContentBlockStyleAttribute in extViewmodels/sdk/Utils.ts): v2 and
 * legacy documents render identically everywhere, AND the UA's default <p> margin is
 * neutralized — without this the caret sits ~14px below the placeholder (no CSS reset
 * exists in the embed environment).
 */
const LEGACY_BLOCK_STYLE = 'margin:0;padding:0;font-size:14px;font-family:"Sora",sans-serif;';

/**
 * The plugin embed host ships a global reset (`ul,li { list-style:none; padding:0; margin:0 }`) and
 * v2 cannot ship a CSS file to undo it, so restore list markers + indent inline on the list itself.
 * `list-style:inherit` on the item is what actually re-shows the marker — the reset's explicit
 * `list-style:none` on `li` beats the inherited type otherwise.
 */
const BULLET_LIST_STYLE = 'list-style-type:disc;padding-left:24px;margin:4px 0;';
const ORDERED_LIST_STYLE = 'list-style-type:decimal;padding-left:24px;margin:4px 0;';
const LIST_ITEM_STYLE = 'list-style:inherit;';

export interface RichTextEditorProps {
	initialContent: string;
	onChange: (html: string, meta: IRichTextChangeMeta) => void;
	/** Fired when the editing surface loses focus (e.g. to commit/persist on blur). */
	onBlur?: () => void;
	/** Shown over the editing surface while the document is empty. */
	placeholder?: string;
	/** Toolbar formatting controls (bold/italic/underline/lists). Default true. */
	showFormatting?: boolean;
	/** Show the Lists dropdown within the formatting controls. Default true. */
	showLists?: boolean;
	/** Show the Link control (hyperlink the selection / edit the link under the caret). Default false. */
	showLink?: boolean;
	/** Show the built-in contact merge-field dropdown. Default true. Ignored when `renderMergeField` is supplied. */
	showMergeField?: boolean;
	/**
	 * Replace the built-in contact merge-field dropdown with a custom control (e.g. a design-system
	 * dropdown). Receives an `insertMergeField` callback to insert the chosen field at the caret.
	 */
	renderMergeField?: (insertMergeField: (field: IMergeFieldAttrs) => void) => ReactNode;
	/** Inline style stamped on each paragraph. Default: the legacy Sora block style. */
	paragraphStyle?: string;
	/** Style overrides merged onto the editing surface (e.g. a themed border for the host app). */
	surfaceStyle?: CSSProperties;
	/** Tokens converted to pills as the user types a known `{{...}}` (see MergeField input rules). */
	mergeFieldTokens?: readonly IMergeFieldToken[];
}

/** Which toolbar dropdown is open; only one at a time. */
type OpenMenu = 'lists' | 'link' | 'merge' | null;

interface IToolbarButton {
	/** Icon child; the accessible name comes from `label`, so the icon is decorative. */
	icon: ReactNode;
	isActive: boolean;
	/** Accessible name (aria-label) — also the value the tests query buttons by. */
	label: string;
	onClick: () => void;
	/** Append a chevron to mark the button as a dropdown trigger. */
	hasChevron?: boolean;
}

function ToolbarButton({ icon, isActive, label, onClick, hasChevron }: IToolbarButton) {
	return (
		<button
			type='button'
			aria-label={label}
			aria-pressed={isActive}
			style={{ ...styles.toolbarButton, ...(isActive ? styles.toolbarButtonActive : undefined) }}
			onClick={onClick}
		>
			{icon}
			{hasChevron && <ChevronDownIcon />}
		</button>
	);
}

export const RichTextEditor = ({
	initialContent,
	onChange,
	onBlur,
	placeholder,
	showFormatting = true,
	showLists = true,
	showLink = false,
	showMergeField = true,
	renderMergeField,
	paragraphStyle = LEGACY_BLOCK_STYLE,
	surfaceStyle,
	mergeFieldTokens,
}: RichTextEditorProps) => {
	const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
	const [linkUrl, setLinkUrl] = useState('');
	const listsMenuRef = useRef<HTMLDivElement>(null);
	const linkMenuRef = useRef<HTMLDivElement>(null);
	const mergeMenuRef = useRef<HTMLDivElement>(null);
	// Focusing the URL field pulls focus off the editing surface, so the range to link is captured
	// when the popover opens rather than read back on Apply.
	const linkSelectionRef = useRef<IRichTextSelection | null>(null);

	useEffect(() => {
		if (!openMenu) {
			return;
		}
		const onMouseDown = (event: MouseEvent) => {
			const target = event.target as Node;
			// Close only when the click lands outside whichever menu is open (and its trigger).
			if (
				!listsMenuRef.current?.contains(target) &&
				!linkMenuRef.current?.contains(target) &&
				!mergeMenuRef.current?.contains(target)
			) {
				setOpenMenu(null);
			}
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setOpenMenu(null);
			}
		};
		document.addEventListener('mousedown', onMouseDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('mousedown', onMouseDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [openMenu]);

	const editor = useRichTextEditor({
		initialContent,
		editorProps: {
			attributes: {
				// Inline on the contenteditable itself (the embed cannot ship CSS files):
				// without min-height the editable is only as tall as its content, leaving
				// most of the surface a dead zone that doesn't focus on click.
				style: 'min-height: 144px; padding: 8px 12px; outline: none;',
			},
		},
		starterKitOptions: {
			paragraph: { HTMLAttributes: { style: paragraphStyle } },
			bulletList: { HTMLAttributes: { style: BULLET_LIST_STYLE } },
			orderedList: { HTMLAttributes: { style: ORDERED_LIST_STYLE } },
			listItem: { HTMLAttributes: { style: LIST_ITEM_STYLE } },
		},
		mergeField: { tokens: mergeFieldTokens },
		immediatelyRender: true,
		shouldRerenderOnTransaction: true,
		onUpdate: (currentEditor, meta) => onChange(currentEditor.getHTML(), meta),
		onBlur: () => onBlur?.(),
	});

	if (!editor) {
		return null;
	}

	// A custom control always renders; the built-in dropdown only when showMergeField is on.
	const showMergeControl = !!renderMergeField || showMergeField;

	const isLinkActive = editor.isActive('link');

	const toggleLinkMenu = () => {
		if (openMenu === 'link') {
			setOpenMenu(null);
			return;
		}
		// Opening from inside an existing link should rewrite the whole link, not split it at the caret.
		// No .focus() here: TipTap defers it to the next frame, which would then steal the caret back
		// from the URL field this click is about to open.
		if (isLinkActive && editor.state.selection.empty) {
			editor.commands.extendMarkRange('link');
		}
		const { from, to } = editor.state.selection;
		linkSelectionRef.current = { from, to };
		const href = editor.getAttributes('link').href;
		setLinkUrl(typeof href === 'string' ? href : '');
		setOpenMenu('link');
	};

	// Only this menu pulls focus into itself, so dismissing it has to hand focus back — otherwise the
	// caret lands on <body> and a keyboard user is stranded outside the document.
	const cancelLinkMenu = () => {
		setOpenMenu(null);
		editor.commands.focus();
	};

	const removeLink = () => {
		editor
			.chain()
			.focus()
			.setTextSelection(linkSelectionRef.current ?? editor.state.selection)
			.extendMarkRange('link')
			.unsetLink()
			.run();
		setOpenMenu(null);
	};

	const applyLink = () => {
		// Clearing the field is how a user unlinks; applyRichTextLink ignores an empty URL, so route it.
		if (!linkUrl.trim()) {
			if (isLinkActive) {
				removeLink();
			} else {
				cancelLinkMenu();
			}
			return;
		}
		applyRichTextLink(editor, linkUrl, linkSelectionRef.current ?? editor.state.selection);
		setOpenMenu(null);
	};

	return (
		<div style={styles.container}>
			<div style={styles.toolbar} role='toolbar' aria-label='Formatting'>
				{showFormatting && (
					<>
						<ToolbarButton
							icon={<BoldIcon />}
							isActive={editor.isActive('bold')}
							label='Bold'
							onClick={() => editor.chain().focus().toggleBold().run()}
						/>
						<ToolbarButton
							icon={<ItalicIcon />}
							isActive={editor.isActive('italic')}
							label='Italic'
							onClick={() => editor.chain().focus().toggleItalic().run()}
						/>
						<ToolbarButton
							icon={<UnderlineIcon />}
							isActive={editor.isActive('underline')}
							label='Underline'
							onClick={() => editor.chain().focus().toggleUnderline().run()}
						/>
						{showLists && (
							<>
								<div style={styles.toolbarDivider} aria-hidden='true' />
								<div ref={listsMenuRef} style={styles.menuWrapper}>
									<ToolbarButton
										icon={<ListIcon />}
										hasChevron
										isActive={openMenu === 'lists' || editor.isActive('bulletList') || editor.isActive('orderedList')}
										label='Lists'
										onClick={() => setOpenMenu(menu => (menu === 'lists' ? null : 'lists'))}
									/>
									{openMenu === 'lists' && (
										<div style={styles.menu}>
											<button
												type='button'
												style={styles.menuItem}
												onClick={() => {
													editor.chain().focus().toggleBulletList().run();
													setOpenMenu(null);
												}}
											>
												<ListIcon />
												Bullet list
											</button>
											<button
												type='button'
												style={styles.menuItem}
												onClick={() => {
													editor.chain().focus().toggleOrderedList().run();
													setOpenMenu(null);
												}}
											>
												<ListOrderedIcon />
												Numbered list
											</button>
										</div>
									)}
								</div>
							</>
						)}
						{(showLink || showMergeControl) && <div style={styles.toolbarDivider} aria-hidden='true' />}
					</>
				)}
				{showLink && (
					<div ref={linkMenuRef} style={styles.menuWrapper}>
						<ToolbarButton
							icon={<LinkIcon />}
							isActive={openMenu === 'link' || isLinkActive}
							label='Link'
							onClick={toggleLinkMenu}
						/>
						{openMenu === 'link' && (
							<div style={styles.linkMenu}>
								<input
									type='text'
									aria-label='Link URL'
									placeholder='Paste or type a link…'
									style={styles.linkInput}
									value={linkUrl}
									onChange={event => setLinkUrl(event.target.value)}
									onKeyDown={event => {
										if (event.key === 'Enter') {
											event.preventDefault();
											applyLink();
										}
										if (event.key === 'Escape') {
											// A host dialog (base-ui) listens for Escape on the document; without stopping the
											// native event too, dismissing this popover would close the composer and lose the note.
											event.stopPropagation();
											event.nativeEvent.stopPropagation();
											cancelLinkMenu();
										}
									}}
									autoFocus
								/>
								{isLinkActive && (
									<button type='button' style={styles.linkMenuRemove} onClick={removeLink}>
										Remove
									</button>
								)}
								<button type='button' style={styles.linkMenuApply} onClick={applyLink}>
									Apply
								</button>
							</div>
						)}
					</div>
				)}
				{showLink && showMergeControl && <div style={styles.toolbarDivider} aria-hidden='true' />}
				{showMergeControl && (
					<div ref={mergeMenuRef} style={styles.menuWrapper}>
						{renderMergeField ? (
							renderMergeField(field => editor.chain().focus().insertMergeField(field).run())
						) : (
							<>
								<ToolbarButton
									icon={<BracesIcon />}
									hasChevron
									isActive={openMenu === 'merge'}
									label='Merge field'
									onClick={() => setOpenMenu(menu => (menu === 'merge' ? null : 'merge'))}
								/>
								{openMenu === 'merge' && (
									<div style={styles.menu}>
										{CONTACT_MERGE_FIELDS.map(field => (
											<button
												key={field.value}
												type='button'
												style={styles.menuItem}
												onClick={() => {
													editor.chain().focus().insertMergeField(field).run();
													setOpenMenu(null);
												}}
											>
												{field.text}
											</button>
										))}
									</div>
								)}
							</>
						)}
					</div>
				)}
			</div>
			<div style={{ ...styles.editorSurface, ...surfaceStyle }}>
				{!!placeholder && editor.isEmpty && <div style={styles.placeholder}>{placeholder}</div>}
				<EditorContent editor={editor} style={styles.editor} />
			</div>
		</div>
	);
};
